import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import {
  LayoutDashboard, Wrench, ScanLine, BookOpen, Crosshair, Receipt,
  ShieldAlert, Building2, FileBarChart2, Users, Sparkles, CalendarDays,
  Settings as SettingsIcon, LogOut, Languages, ChevronDown, ChevronRight,
  Briefcase, Database, Shield, DollarSign, Cpu, Truck, Tags, IdCard, Hash,
  ClipboardList, Activity, AlertOctagon, BookMarked, Trophy, Star, Megaphone,
  Key, FileCheck2, Crosshair as CrosshairIcon, MapPin, PackageSearch, Target, TrendingUp, Percent,
  UserCheck, KeyRound, FileText,
} from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import { PWAInstallButton } from "@/components/PWAInstallButton";
import SetupWizardBanner from "@/components/SetupWizardBanner";
import BrandLogo from "@/components/BrandLogo";
import { useDomTranslator } from "@/hooks/useDomTranslator";
import InstallPrompt from "@/components/InstallPrompt";
import WelcomeTour from "@/components/WelcomeTour";

const NAV_GROUPS = [
  {
    label: "Dashboard",
    items: [
      { to: "/", icon: LayoutDashboard, name: "Dashboard", key: "dashboard", end: true },
      { to: "/my-workspace", icon: Trophy, name: "My Workspace", key: "my-workspace" },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/jobs", icon: Briefcase, name: "Jobs", key: "jobs" },
      { to: "/calibration-reports", icon: FileCheck2, name: "Calibration Reports", key: "cal-reports" },
      { to: "/work-orders", icon: ClipboardList, name: "Work Orders", key: "work-orders" },
      { to: "/fulltime", icon: Truck, name: "Ordens FullTime", key: "fulltime" },
      { to: "/scheduling", icon: CalendarDays, name: "Scheduling", key: "scheduling" },
      { to: "/equipment", icon: PackageSearch, name: "Equipment Inventory", key: "equipment" },
      { to: "/claims", icon: ShieldAlert, name: "Insurance Claims", key: "claims" },
    ],
  },
  {
    label: "Sales & CRM",
    items: [
      { to: "/sales", icon: Target, name: "Pipeline", key: "sales-pipeline" },
      { to: "/sales/prospects", icon: ClipboardList, name: "Prospects", key: "sales-prospects" },
      { to: "/quotes", icon: FileText, name: "Orçamentos", key: "quotes" },
      { to: "/apresentacao", icon: Sparkles, name: "Apresentação", key: "presentation" },
      { to: "/sales/commissions", icon: Percent, name: "Commissions", key: "sales-commissions" },
      { to: "/sales/reports", icon: TrendingUp, name: "Sales Reports", key: "sales-reports" },
    ],
  },
  {
    label: "Master Data",
    items: [
      { to: "/master/customers", icon: Users, name: "Customers", key: "m-customers" },
      { to: "/master/insurance", icon: Shield, name: "Insurance Companies", key: "m-insurance" },
      { to: "/master/body-shops", icon: Building2, name: "Body Shops", key: "m-bodyshops" },
      { to: "/master/technicians", icon: IdCard, name: "Technicians", key: "m-techs" },
      { to: "/master/vendors", icon: Truck, name: "Vendors", key: "m-vendors" },
      { to: "/master/rate-tables", icon: Tags, name: "Rate Tables", key: "m-rates" },
      { to: "/master/custom-services", icon: Wrench, name: "Custom Services", key: "m-custom-svc" },
      { to: "/master/locations", icon: MapPin, name: "Locations", key: "m-locations" },
      { to: "/master/targets", icon: CrosshairIcon, name: "Calibration Targets", key: "m-targets" },
      { to: "/master/positions", icon: BookMarked, name: "OEM Positions", key: "m-positions" },
    ],
  },
  {
    label: "ADAS Intelligence",
    items: [
      { to: "/adas", icon: ScanLine, name: "ADAS Analysis", key: "adas" },
      { to: "/vin", icon: Hash, name: "VIN Decoder", key: "vin" },
      { to: "/biblioteca", icon: BookOpen, name: "Biblioteca TrueLine", key: "biblioteca" },
      { to: "/settings/alldata-bridge", icon: Target, name: "ALLDATA Bridge", key: "alldata-bridge" },
    ],
  },
  {
    label: "Financial",
    items: [
      { to: "/billing", icon: Receipt, name: "Billing", key: "billing" },
      { to: "/cash-flow", icon: DollarSign, name: "Cash Flow", key: "cash-flow" },
      { to: "/reports", icon: FileBarChart2, name: "Reports", key: "reports" },
    ],
  },
  {
    label: "Admin",
    items: [
      { to: "/admin/users", icon: Users, name: "User Management", key: "user-mgmt", adminOnly: true },
      { to: "/admin/pending-users", icon: UserCheck, name: "Pending Approvals", key: "pending-users" },
      { to: "/admin/invite-codes", icon: KeyRound, name: "Invite Codes", key: "invite-codes" },
      { to: "/body-shop", icon: Building2, name: "Body Shop Portal", key: "bs-portal" },
      { to: "/compliance", icon: FileCheck2, name: "Compliance Reports", key: "compliance" },
      { to: "/admin/api-keys", icon: Key, name: "API Keys", key: "api-keys" },
      { to: "/admin/activity", icon: Activity, name: "Activity Logs", key: "activity" },
      { to: "/admin/data-reset", icon: AlertOctagon, name: "Data Reset Center", key: "data-reset" },
    ],
  },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { lang, switchLang } = useLang();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState({});

  // Runtime DOM translator — turns the whole app into the chosen language on the fly
  const { isTranslating } = useDomTranslator(lang);

  const toggle = (label) => setCollapsed((c) => ({ ...c, [label]: !c[label] }));

  return (
    <div className="min-h-screen bg-[#030712] text-slate-100 flex">
      <aside className="w-64 shrink-0 border-r border-slate-800/80 bg-[#050a17] flex flex-col h-screen sticky top-0">
        <div className="p-5 border-b border-slate-800/80 flex items-center gap-2.5">
          <BrandLogo size="md" />
          <div>
            <div className="font-display font-semibold text-base tracking-tight">TrueLine</div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-slate-500 font-bold">Calibration · ADAS</div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-3" data-testid="sidebar-nav">
          {NAV_GROUPS.map((group) => {
            const isCollapsed = collapsed[group.label];
            return (
              <div key={group.label}>
                {group.items.length > 1 ? (
                  <button
                    onClick={() => toggle(group.label)}
                    data-testid={`group-${group.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="w-full flex items-center justify-between px-2 py-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 hover:text-slate-300 transition-colors"
                  >
                    <span>{group.label}</span>
                    {isCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  </button>
                ) : null}
                {!isCollapsed && (
                  <div className="space-y-0.5 mt-1">
                    {group.items.filter((it) => !it.adminOnly || user?.role === "admin").map((it) => (
                      <NavLink
                        key={it.to}
                        to={it.to}
                        end={it.end}
                        data-testid={`nav-${it.key}`}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                            isActive
                              ? "bg-blue-600/15 text-blue-400 font-medium border border-blue-500/20"
                              : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/40"
                          }`
                        }
                      >
                        <it.icon className="w-4 h-4" />
                        <span>{it.name}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="p-3 border-t border-slate-800/80 space-y-2">
          <NavLink
            to="/admin/duplicates"
            data-testid="nav-duplicates"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                isActive ? "bg-amber-500/15 text-amber-300" : "text-slate-400 hover:bg-slate-800/40 hover:text-slate-100"
              }`
            }
          >
            <ChevronRight className="w-4 h-4" />
            <span>Duplicates Review</span>
          </NavLink>
          <NavLink
            to="/admin/tech-guide-feedback"
            data-testid="nav-tech-guide-feedback"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                isActive ? "bg-cyan-500/15 text-cyan-300" : "text-slate-400 hover:bg-slate-800/40 hover:text-slate-100"
              }`
            }
          >
            <ChevronRight className="w-4 h-4" />
            <span>Tech Guide Feedback</span>
          </NavLink>
          <NavLink
            to="/settings"
            data-testid="nav-settings"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                isActive ? "bg-blue-600/15 text-blue-400" : "text-slate-400 hover:bg-slate-800/40 hover:text-slate-100"
              }`
            }
          >
            <SettingsIcon className="w-4 h-4" />
            <span>Settings</span>
          </NavLink>

          <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-slate-900/50 border border-slate-800/80">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center text-white font-bold text-xs">
              {user?.name?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium truncate">{user?.name}</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wide">{user?.role}</div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={() => { logout(); navigate("/login"); }}
              className="text-slate-400 hover:text-red-400 transition-colors"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <header className="sticky top-0 z-20 bg-[#030712]/85 backdrop-blur-xl border-b border-slate-800/80 px-8 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-bold">
              {user?.company_name || "TrueLine"}
            </div>
            <span className="text-slate-700">/</span>
            <span className="text-xs text-slate-400 font-mono">PROD</span>
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot" />
          </div>
          <div className="flex items-center gap-3">
            <PWAInstallButton variant="compact" />
            <NotificationBell />
            <button
              data-testid="header-lang-toggle"
              onClick={() => {
                const next = lang === "en" ? "pt" : "en";
                switchLang(next);
                // Switching to EN: reload so previously-translated DOM nodes
                // reset to their original English text. Switching to PT:
                // useDomTranslator picks it up reactively, no reload needed.
                if (next === "en") {
                  setTimeout(() => window.location.reload(), 50);
                }
              }}
              className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 hover:text-blue-400 flex items-center gap-1.5 transition-colors"
              title={lang === "en" ? "Switch to Portuguese (Brazil)" : "Switch to English"}
            >
              <Languages className={`w-3.5 h-3.5 ${isTranslating ? "animate-pulse text-blue-400" : ""}`} />
              {isTranslating ? "…" : (lang === "en" ? "EN" : "PT")}
            </button>
          </div>
        </header>
        <div className="p-8">
          <SetupWizardBanner />
          <Outlet />
        </div>
      </main>
      <InstallPrompt />
      <WelcomeTour />
    </div>
  );
}
